import { useState, useEffect } from 'react'
import { useAuth } from '../lib/auth'
import { supabase } from '../lib/supabase'

const STORAGE_KEY = 'placed:impersonating_org_id'
const EVENT_NAME = 'placed:impersonation-change'

function readImpersonation() {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) || null
  } catch {
    return null
  }
}

function writeImpersonation(orgId) {
  if (typeof window === 'undefined') return
  try {
    if (orgId) window.sessionStorage.setItem(STORAGE_KEY, orgId)
    else window.sessionStorage.removeItem(STORAGE_KEY)
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { orgId } }))
  } catch {
    /* ignore */
  }
}

export function useOrg() {
  const { profile } = useAuth()
  const [impersonatingOrgId, setImpersonatingOrgId] = useState(readImpersonation)
  const [impersonatedOrg, setImpersonatedOrg] = useState(null)

  // Keep state in sync with sessionStorage across components / tabs.
  useEffect(() => {
    function onChange() {
      setImpersonatingOrgId(readImpersonation())
    }
    window.addEventListener(EVENT_NAME, onChange)
    window.addEventListener('storage', onChange)
    return () => {
      window.removeEventListener(EVENT_NAME, onChange)
      window.removeEventListener('storage', onChange)
    }
  }, [])

  // When impersonating, fetch the impersonated org record so consumers can read its name.
  useEffect(() => {
    if (!impersonatingOrgId) {
      setImpersonatedOrg(null)
      return
    }
    let cancelled = false
    supabase.from('organizations').select('*').eq('id', impersonatingOrgId).single()
      .then(({ data }) => { if (!cancelled) setImpersonatedOrg(data || null) })
    return () => { cancelled = true }
  }, [impersonatingOrgId])

  const org = impersonatingOrgId ? impersonatedOrg : (profile?.organizations ?? null)
  const orgId = impersonatingOrgId || profile?.org_id || null
  const isImpersonating = !!impersonatingOrgId

  function impersonate(targetOrgId) {
    writeImpersonation(targetOrgId)
    setImpersonatingOrgId(targetOrgId)
  }

  function stopImpersonating() {
    writeImpersonation(null)
    setImpersonatingOrgId(null)
  }

  return {
    org,
    orgId,
    isImpersonating,
    impersonate,
    stopImpersonating,
  }
}
